import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import type { DataSource, UpdateDataSourceRequest } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
  dataSource: DataSource;
  submitting: boolean;
  onSubmit: (body: UpdateDataSourceRequest) => void;
  onCancel?: () => void;
}

/**
 * 엑셀 데이터소스 전용 단순 편집 폼.
 * 외부 접속 정보 (host/port/database/username/password/sslMode) 는 의미 없으므로 노출 X.
 * 이름·설명만 편집 가능, 종류/스키마/시트 목록은 read-only 표시.
 */
export function ExcelEditForm({ dataSource, submitting, onSubmit, onCancel }: Props) {
  const [name, setName] = useState(dataSource.name);
  const [description, setDescription] = useState(dataSource.description ?? '');
  const [errors, setErrors] = useState<{ name?: string }>({});

  const tablesQuery = useQuery({
    queryKey: ['admin', 'data-sources', dataSource.id, 'tables'],
    queryFn: () => api.dataSourceTableList(dataSource.id),
  });
  const tables = tablesQuery.data?.tables ?? [];
  const schemaName = dataSource.schemaFilter[0] ?? 'data_source_excel';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: { name?: string } = {};
    if (name.trim().length === 0) next.name = '이름을 입력하세요.';
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({
      name: name.trim(),
      description: description.trim().length === 0 ? null : description.trim(),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <Field label="이름" htmlFor="ds-name" error={errors.name} hint="식별용. 공백 가능.">
          <Input
            id="ds-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="예: 2026 매출원장"
          />
        </Field>
        <Field label="종류" htmlFor="ds-kind" hint="엑셀·CSV 데이터소스 (변경 불가)">
          <div
            id="ds-kind"
            className="flex h-10 items-center rounded-md border border-border bg-surface-muted/40 px-3 text-sm text-foreground-muted"
          >
            Excel·CSV
          </div>
        </Field>
      </div>

      <Field
        label="영구 스키마"
        htmlFor="ds-schema"
        hint="모든 엑셀 데이터소스가 공유하는 단일 스키마 (시스템 관리)"
      >
        <div
          id="ds-schema"
          className="flex h-10 items-center rounded-md border border-border bg-surface-muted/40 px-3 font-mono text-xs text-foreground-muted"
        >
          {schemaName}
        </div>
      </Field>

      <Field
        label="등록된 시트 (테이블)"
        htmlFor="ds-tables"
        hint={
          tablesQuery.isLoading
            ? '불러오는 중…'
            : `${tables.length} 개. SQL 작성 시 "${schemaName}"."<테이블명>" 형식 사용.`
        }
      >
        {tables.length > 0 ? (
          <div className="overflow-x-auto rounded-md border border-border bg-surface">
            <table className="w-full text-xs">
              <thead className="bg-surface-muted/40 text-[10px] uppercase tracking-wider text-foreground-muted">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">SQL 테이블명</th>
                  <th className="px-3 py-2 text-left font-medium">설명</th>
                  <th className="px-3 py-2 text-right font-medium">예상 행수</th>
                </tr>
              </thead>
              <tbody>
                {tables.map((t) => (
                  <tr key={t.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 font-mono">{t.tableName}</td>
                    <td className="px-3 py-2 text-foreground-muted">
                      {t.description ?? <span className="text-foreground-faint">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {t.estimatedRowCount?.toLocaleString() ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p
            id="ds-tables"
            className="rounded-md border border-dashed border-border bg-surface-muted/20 px-3 py-4 text-center text-xs text-foreground-muted"
          >
            등록된 시트가 없습니다.
          </p>
        )}
      </Field>

      <Field
        label="설명 (관리자용 메모)"
        htmlFor="ds-desc"
        hint="등록 시 LLM 자동 요약이 들어가 있을 수 있습니다. 자유롭게 편집하세요."
      >
        <textarea
          id="ds-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus-visible:outline-none"
          placeholder="이 데이터소스의 출처/용도/주의사항 등."
        />
      </Field>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            취소
          </Button>
        ) : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? '저장 중…' : '변경사항 저장'}
        </Button>
      </div>
    </form>
  );
}
